import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TablePagination,
  TextField,
  Box,
  InputAdornment,
  Alert,
  Grid,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PublicIcon from "@mui/icons-material/Public";
import { useDebounce } from "utils/hooks/useDebounce";
import { motion } from "framer-motion";
import { Header } from "components/shared";
import { useUtility } from "utils/context/UtilityContext";
import { fetchPaginatedSms, fetchSingleMessage, SmsMessage } from "utils";

type Order = "asc" | "desc";
type SortableColumns = "date" | "address" | "body" | "market" | "deviceCountry";

export const SmsMessagesTable = () => {
  const { marginBottomHeader } = useUtility();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Data and UI State
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<SmsMessage | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  // Query State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [sortBy, setSortBy] = useState<SortableColumns>("date");
  const [order, setOrder] = useState<Order>("desc");

  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const debouncedCountryFilter = useDebounce(countryFilter, 500);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchPaginatedSms({
        page,
        limit: rowsPerPage,
        searchQuery: debouncedSearchQuery,
        filterByCountry: debouncedCountryFilter,
        sortBy,
        order,
      });
      setMessages(response.data);
      setTotalItems(response.meta.totalItems);
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearchQuery, debouncedCountryFilter, sortBy, order]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleSort = (property: SortableColumns) => {
    const isAsc = sortBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
  };

  const handleRowClick = async (messageToFetch: SmsMessage) => {
    try {
      const message = await fetchSingleMessage(messageToFetch.id, messageToFetch.defaultMarket);
      setSelectedMessage(message);
      setDialogOpen(true);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCloseDialog = () => setDialogOpen(false);
  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const tableHeaders: { id: SortableColumns; label: string }[] = [
    { id: "date", label: "Date" },
    { id: "address", label: "Sender" },
    { id: "deviceCountry", label: "Country" },
    { id: "body", label: "Message Preview" },
  ];

  const renderSkeletonRows = () => {
    return Array.from(new Array(rowsPerPage)).map((_, index) => (
      <TableRow key={index}>
        {tableHeaders.map((header) => (
          <TableCell key={header.id}><Skeleton variant="text" /></TableCell>
        ))}
      </TableRow>
    ));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`${marginBottomHeader} flex flex-col items-center justify-between gap-4 overflow-x-hidden fancy-scroll min-h-screen`}
    >
      <Header />
      <Box sx={{ p: isMobile ? 2 : 4, width: '100%' }}>
        <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: "8px", width: '100%' }}>
          <Typography variant="h6" align="center" gutterBottom sx={{ color: "#104b7b", mb: 3 }}>
            SMS Messages
          </Typography>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by Sender or Message..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon /></InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Filter by Country Code..."
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><PublicIcon /></InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#104b7b" }}>
                  {tableHeaders.map((headCell) => (
                    <TableCell key={headCell.id} sx={{ color: "#ffffff" }}>
                      <TableSortLabel
                        active={sortBy === headCell.id}
                        direction={sortBy === headCell.id ? order : 'asc'}
                        onClick={() => handleSort(headCell.id)}
                        sx={{ color: "#ffffff", '&:hover': { color: "#d1e0e5" }, '& .MuiTableSortLabel-icon': { color: "#ffffff !important" } }}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  renderSkeletonRows()
                ) : messages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableHeaders.length} align="center">
                      <Typography sx={{ p: 4 }}>No messages found. Try adjusting your filters.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  messages.map((message, index) => (
                    <TableRow
                      hover
                      key={message.id}
                      onClick={() => handleRowClick(message)}
                      sx={{ cursor: "pointer", backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff", "&:hover": { backgroundColor: "#e3f2fd" } }}
                    >
                      <TableCell>{new Date(message.date).toLocaleString()}</TableCell>
                      <TableCell>{message.address}</TableCell>
                      <TableCell>{message.defaultMarket}</TableCell>
                      <TableCell>{message.body.length > 50 ? `${message.body.slice(0, 50)}...` : message.body}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[25, 50, 70, 100]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>

      {selectedMessage && (
        <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm" fullScreen={isMobile}>
          <DialogTitle sx={{ backgroundColor: "#104b7b", color: "#ffffff" }}>Message Details</DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography gutterBottom><strong>From:</strong> {selectedMessage.address}</Typography>
            <Typography gutterBottom><strong>Date:</strong> {new Date(selectedMessage.date).toLocaleString()}</Typography>
            <Typography gutterBottom><strong>Country:</strong> {selectedMessage.defaultMarket}</Typography>
            <Typography><strong>Message:</strong> {selectedMessage.body}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} sx={{ color: "#104b7b" }}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </motion.div>
  );
};

